import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { TableOperationHeader } from '@getsynapse/design-system';
import {
  setSearchParam,
  exportUsersStatus,
  setFilters,
} from 'state/UsersManagement/usersManagementSlice';
import { SLICE_STATUS } from 'utils/constants';
import MoreActionsPopup, {
  OptionProps,
} from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import ExportIcon from 'assets/icons/export.svg';
import FilterButton from 'Organisms/TableHeaderActionButtons/FilterButton';
import UsersSidePanelFilters from './UsersSidePanelFilters/UsersSidePanelFilters';
import { UserFilters } from 'utils/types/filters';
import AppliedFiltersTags from 'Organisms/AppliedFiltersTags/AppliedFiltersTags';
import useUserFilters from './hooks/useUserFilters';

interface UsersTableHeaderProps {
  isFilterButtonDisabled: boolean;
  isExportButtonDisabled: boolean;
  handleExport: (callback: () => void) => void;
  savedFilters: UserFilters;
  saveFilters: (updatedFilters: UserFilters) => void;
  toggleFilterComponent: () => void;
  showFilterComponent: boolean;
}

const UsersTableHeader: React.FC<UsersTableHeaderProps> = ({
  isFilterButtonDisabled = true,
  isExportButtonDisabled = true,
  handleExport,
  savedFilters,
  saveFilters,
  toggleFilterComponent,
  showFilterComponent,
}) => {
  const dispatch = useDispatch();
  const { getOptionsbyKey } = useUserFilters(savedFilters);
  const exportStatus = useSelector(exportUsersStatus);
  const [searchParamValue, setSearchParamValue] = useState('');

  const handleSearch = (value: string) => {
    setSearchParamValue(value);
    dispatch(setSearchParam(value));
  };

  const handleUpdateFilters = (updatedFilters: UserFilters) => {
    dispatch(setFilters(updatedFilters));
    saveFilters(updatedFilters);
  };

  const moreActionsList = [
    {
      label: intl.get('USERS_PAGE.TABLE.EXPORT.EXPORT_USERS'),
      selectOption: handleExport,
      disabled: isExportButtonDisabled,
      tooltip: {
        text: isExportButtonDisabled
          ? intl.get('USERS_PAGE.TABLE.EXPORT.EXPORT_TOOLTIP')
          : '',
      },
      iconSrc: ExportIcon,
      dataCy: 'export-users-button',
      loading: exportStatus === SLICE_STATUS.LOADING,
    },
  ] as OptionProps[];

  return (
    <>
      <UsersSidePanelFilters
        savedFilters={savedFilters}
        onClose={toggleFilterComponent}
        open={showFilterComponent}
        onUpdateFilters={handleUpdateFilters}
      />
      <TableOperationHeader
        searchProps={{
          placeholder: intl.get('SEARCH_PLACEHOLDER'),
        }}
        className='border-0 rounded-b-none bg-neutral-white'
        inputValue={searchParamValue}
        setInputValue={handleSearch}
        actionButtons={
          <>
            <FilterButton
              aria-pressed={showFilterComponent}
              disabled={isFilterButtonDisabled}
              onToggleFilter={toggleFilterComponent}
            />
            <MoreActionsPopup options={moreActionsList} />
          </>
        }
      />
      <AppliedFiltersTags<UserFilters>
        savedFilters={savedFilters}
        onUpdateFilters={handleUpdateFilters}
        onDisplayAllFilters={toggleFilterComponent}
        getOptionsbyKey={getOptionsbyKey}
        dataTestid='applied-programs-filters-tags'
      />
    </>
  );
};

export default UsersTableHeader;
